import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Folder, FolderOpen } from 'lucide-react';
import { CategoryTreeNode, CreateCategoryRequest, UpdateCategoryRequest } from '../types';
import { apiClient } from '../api/client';

interface CategoryManagerProps {
  className?: string;
}

interface CategoryFormData {
  name: string;
  parent_id: number | null;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({ className = "" }) => {
  const [categories, setCategories] = useState<CategoryTreeNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());
  const [editingCategory, setEditingCategory] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<CategoryFormData>({ name: '', parent_id: null });
  const [error, setError] = useState<string>('');

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const tree = await apiClient.getCategoriesTree();
      setCategories(tree);
    } catch (error) {
      console.error('Failed to load categories:', error);
      setError('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (categoryId: number) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedNodes(newExpanded);
  };

  const startEdit = (category: CategoryTreeNode) => {
    setEditingCategory(category.id);
    setFormData({ name: category.name, parent_id: category.parent_id });
    setShowAddForm(false);
  };

  const startAdd = (parentId?: number) => {
    setFormData({ name: '', parent_id: parentId || null });
    setEditingCategory(null);
    setShowAddForm(true);
  };

  const cancelEdit = () => {
    setEditingCategory(null);
    setShowAddForm(false);
    setFormData({ name: '', parent_id: null });
    setError('');
  };

  const saveCategory = async () => {
    if (!formData.name.trim()) {
      setError('Category name is required');
      return;
    }

    try {
      if (editingCategory) {
        // Update existing category
        await apiClient.updateCategory(editingCategory, formData as UpdateCategoryRequest);
      } else {
        // Create new category
        await apiClient.createCategory(formData as CreateCategoryRequest);
      }

      await loadCategories();
      cancelEdit();
    } catch (error: any) {
      console.error('Failed to save category:', error);
      setError(error.message || 'Failed to save category');
    }
  };

  const deleteCategory = async (categoryId: number) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }

    try {
      await apiClient.deleteCategory(categoryId);
      await loadCategories();
    } catch (error: any) {
      console.error('Failed to delete category:', error);
      alert(error.message || 'Failed to delete category');
    }
  };

  const renderCategory = (category: CategoryTreeNode, level = 0): React.ReactNode => {
    const isExpanded = expandedNodes.has(category.id);
    const hasChildren = category.children && category.children.length > 0;
    const isEditing = editingCategory === category.id;

    return (
      <div key={category.id}>
        <div
          className={`flex items-center gap-2 py-2 px-3 hover:bg-white/5 rounded-md group`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
        >
          {/* Expand/Collapse button */}
          {hasChildren && (
            <button
              onClick={() => toggleExpanded(category.id)}
              className="p-1 hover:bg-white/10 rounded"
            >
              {isExpanded ? (
                <FolderOpen className="h-4 w-4 text-slate-400" />
              ) : (
                <Folder className="h-4 w-4 text-slate-400" />
              )}
            </button>
          )}

          {/* Category name/edit form */}
          {isEditing ? (
            <div className="flex-1 flex gap-2">
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="flex-1 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm"
                placeholder="Category name"
                autoFocus
              />
              <select
                value={formData.parent_id || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  parent_id: e.target.value ? parseInt(e.target.value) : null
                })}
                className="px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm"
              >
                <option value="">Root level</option>
                {renderParentOptions(categories, category.id)}
              </select>
            </div>
          ) : (
            <span className="flex-1 text-white">{category.name}</span>
          )}

          {/* Action buttons */}
          <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
            {isEditing ? (
              <>
                <button
                  onClick={saveCategory}
                  className="p-1 hover:bg-green-500/20 text-green-400 rounded"
                  title="Save"
                >
                  ✓
                </button>
                <button
                  onClick={cancelEdit}
                  className="p-1 hover:bg-red-500/20 text-red-400 rounded"
                  title="Cancel"
                >
                  ✕
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => startAdd(category.id)}
                  className="p-1 hover:bg-[var(--accent-gold)]/20 text-[var(--accent-gold)] rounded"
                  title="Add subcategory"
                >
                  <Plus className="h-3 w-3" />
                </button>
                <button
                  onClick={() => startEdit(category)}
                  className="p-1 hover:bg-[var(--accent-gold)]/20 text-[var(--accent-gold)] rounded"
                  title="Edit"
                >
                  <Edit2 className="h-3 w-3" />
                </button>
                <button
                  onClick={() => deleteCategory(category.id)}
                  className="p-1 hover:bg-red-500/20 text-red-400 rounded"
                  title="Delete"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div>
            {category.children.map(child => renderCategory(child, level + 1))}
          </div>
        )}

        {/* Add form for this level */}
        {showAddForm && formData.parent_id === category.id && (
          <div
            className="flex items-center gap-2 py-2 px-3 bg-white/5 rounded-md"
            style={{ paddingLeft: `${(level + 1) * 20 + 12}px` }}
          >
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="flex-1 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm"
              placeholder="New category name"
              autoFocus
            />
            <select
              value={formData.parent_id || ''}
              onChange={(e) => setFormData({
                ...formData,
                parent_id: e.target.value ? parseInt(e.target.value) : null
              })}
              className="px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-sm"
            >
              <option value="">Root level</option>
              {renderParentOptions(categories, category.id)}
            </select>
            <button
              onClick={saveCategory}
              className="px-2 py-1 bg-[var(--accent-gold)] hover:bg-[#d4b896] text-white rounded text-sm"
            >
              Add
            </button>
            <button
              onClick={cancelEdit}
              className="px-2 py-1 bg-slate-600 hover:bg-slate-700 text-white rounded text-sm"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderParentOptions = (cats: CategoryTreeNode[], excludeId?: number): React.ReactNode => {
    return cats.map(cat => (
      <React.Fragment key={cat.id}>
        {cat.id !== excludeId && (
          <option value={cat.id}>{cat.name}</option>
        )}
        {cat.children && cat.children.length > 0 && (
          <>
            {renderParentOptions(cat.children, excludeId)}
          </>
        )}
      </React.Fragment>
    ));
  };

  if (loading) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <div className="text-slate-400">Loading categories...</div>
      </div>
    );
  }

  return (
    <div className={className}>
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 text-red-300 rounded-md">
          {error}
        </div>
      )}

      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-white">Categories</h3>
        <button
          onClick={() => startAdd()}
          className="flex items-center gap-2 px-3 py-2 bg-[var(--accent-gold)] hover:bg-[#d4b896] text-white rounded-md transition-colors"
        >
          <Plus className="h-4 w-4" />
          Add Category
        </button>
      </div>

      <div className="bg-white/5 rounded-lg border border-white/10 overflow-hidden">
        {categories.length === 0 && !showAddForm ? (
          <div className="text-center py-8">
            <div className="text-slate-400 mb-4">No categories yet</div>
            <button
              onClick={() => startAdd()}
              className="px-4 py-2 bg-[var(--accent-gold)] hover:bg-[#d4b896] text-white rounded-md transition-colors"
            >
              Create your first category
            </button>
          </div>
        ) : (
          <div className="p-4 space-y-2">
            {/* Root-level add form */}
            {showAddForm && formData.parent_id === null && (
              <div className="flex items-center gap-2 p-3 bg-white/5 rounded-md border border-[var(--accent-gold)]/30">
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  onKeyPress={(e) => e.key === 'Enter' && saveCategory()}
                  className="flex-1 px-3 py-2 bg-white/10 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-[var(--accent-gold)]"
                  placeholder="New category name"
                  autoFocus
                />
                <button
                  onClick={saveCategory}
                  className="px-4 py-2 bg-[var(--accent-gold)] hover:bg-[#d4b896] text-white rounded-md text-sm font-medium transition-colors"
                >
                  Add
                </button>
                <button
                  onClick={cancelEdit}
                  className="px-4 py-2 bg-slate-600 hover:bg-slate-700 text-white rounded-md text-sm transition-colors"
                >
                  Cancel
                </button>
              </div>
            )}
            
            {/* Existing categories */}
            {categories.length > 0 && (
              <div className="h-[calc(100vh-400px)] min-h-[500px] overflow-y-auto">
                {categories.map(category => renderCategory(category))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CategoryManager;